// Page « Mon organisation » (spec § 3, organisations partenaires) : nom, membres et pipeline de l'organisation.
// Son administrateur (abonné) invite un collègue, le suspend ou lui confie l'administration, et règle les étapes de
// ses affaires propres ; les autres membres lisent. Les affaires transmises par le client gardent ses étapes.
// Droits vérifiés par la base (`set_org_pipeline`, `set_org_name`) et par la fonction `users`.
import { ArrowDown, ArrowUp, ChevronRight, Plus, Trash2 } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal, PartnerOrg, Sale } from "../types";
import { normalizeStages, orgSeats } from "./partnerOrg";
import { stageCounts } from "./stageUsage";
import { useOfferOpen } from "./OfferPage";
import { usePartnerOrg } from "./usePartnerOrg";

/** Message lisible d'une erreur de la base ou de la fonction `users`. */
const errorMessage = async (error: unknown) => {
  const context = (error as { context?: Response })?.context;
  if (context?.json) {
    try {
      const body = await context.json();
      // Organisation pleine : message traduit, le texte de la fonction est en anglais.
      if (body?.code === "org_full") return "resources.deals.org.full_error";
      return body?.message ?? String(error);
    } catch {
      return String(error);
    }
  }
  return (error as { message?: string })?.message ?? String(error);
};

export const OrgPage = () => {
  const translate = useTranslate();
  const offerOpen = useOfferOpen();
  const { org, orgAdmin, partner, subscriber, showOrg, isPending } =
    usePartnerOrg();
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#pipeline")
      document.getElementById("pipeline")?.scrollIntoView();
  }, [location.hash, org]);
  if (isPending) return null;
  // Un invité qui tape l'adresse : l'organisation s'ouvre à l'abonnement.
  if (partner && !subscriber)
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {offerOpen ? (
          <>
            {translate("resources.deals.org.invited_only")}{" "}
            <Link to="/offer" className="underline">
              {translate("resources.projects.offer.title")}
            </Link>
          </>
        ) : (
          translate("resources.deals.org.invited_only_closed")
        )}
      </p>
    );
  if (!showOrg || !org)
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {translate("resources.deals.org.none")}
      </p>
    );
  return (
    <div className="mt-2 pb-16 flex flex-col gap-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-semibold">
          {translate("resources.deals.org.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate(
            orgAdmin
              ? "resources.deals.org.intro_admin"
              : "resources.deals.org.intro_member",
          )}
        </p>
      </div>
      <OrgName org={org} editable={orgAdmin} />
      <OrgMembers org={org} editable={orgAdmin} />
      <OrgPipelineEditor
        key={JSON.stringify(org)}
        org={org}
        editable={orgAdmin}
      />
    </div>
  );
};

OrgPage.path = "/organisation";

/** Lien vers la page, dans les paramètres sur téléphone, pour les membres abonnés d'une organisation. */
export const OrgSection = () => {
  const translate = useTranslate();
  const { showOrg } = usePartnerOrg();
  if (!showOrg) return null;
  return (
    <ItemGroup className="rounded-lg border overflow-hidden">
      <Item asChild size="sm" className="cursor-pointer">
        <Link to={OrgPage.path}>
          <ItemContent>
            <ItemTitle className="font-normal">
              {translate("resources.deals.org.title")}
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <ChevronRight className="size-4 text-muted-foreground" />
          </ItemActions>
        </Link>
      </Item>
    </ItemGroup>
  );
};

const OrgName = ({ org, editable }: { org: PartnerOrg; editable: boolean }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [name, setName] = useState(org.name);
  const [saving, setSaving] = useState(false);
  if (!editable)
    return (
      <p className="text-lg font-medium" data-testid="org-name">
        {org.name}
      </p>
    );
  const save = async () => {
    setSaving(true);
    const { error } = await getSupabaseClient().rpc("set_org_name", {
      value: name,
    });
    setSaving(false);
    if (error) return notify(await errorMessage(error), { type: "error" });
    notify("resources.deals.org.saved", { type: "success" });
    refresh();
  };
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex flex-1 flex-col gap-1">
        <Label htmlFor="org-name">
          {translate("resources.deals.org.name")}
        </Label>
        <Input
          id="org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <Button
        variant="outline"
        disabled={saving || !name.trim() || name.trim() === org.name}
        onClick={save}
      >
        {translate("ra.action.save")}
      </Button>
    </div>
  );
};

const OrgMembers = ({
  org,
  editable,
}: {
  org: PartnerOrg;
  editable: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [busy, setBusy] = useState<number | null>(null);
  const { data: members } = useGetList<Sale>("sales", {
    filter: { org_id: org.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: limit } = useQuery({
    queryKey: ["org_member_limit"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("org_member_limit");
      if (error) throw error;
      return data as number;
    },
    staleTime: Infinity,
  });
  const seats = orgSeats(members ?? [], limit);
  const patch = async (member: Sale, changes: Partial<Sale>) => {
    setBusy(Number(member.id));
    const { error } = await getSupabaseClient().functions.invoke("users", {
      method: "PATCH",
      body: {
        org_member_id: member.id,
        disabled: changes.disabled ?? !!member.disabled,
        org_admin: changes.org_admin ?? !!member.org_admin,
      },
    });
    setBusy(null);
    if (error) return notify(await errorMessage(error), { type: "error" });
    refresh();
  };
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-semibold">
        {translate("resources.deals.org.members")}
        {seats.limit != null && (
          <span className="text-muted-foreground ml-2 text-sm font-normal">
            {translate("resources.deals.org.seats", {
              active: seats.active,
              limit: seats.limit,
            })}
          </span>
        )}
      </h3>
      <Card className="py-0">
        <CardContent className="flex flex-col divide-y px-0">
          {(members ?? []).map((member) => {
            const me = member.id === identity?.id;
            return (
              <div
                key={member.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {member.first_name} {member.last_name}
                    </span>
                    {member.org_admin && (
                      <Badge variant="secondary">
                        {translate("resources.deals.org.admin")}
                      </Badge>
                    )}
                    {member.disabled && (
                      <Badge variant="outline">
                        {translate("resources.deals.org.disabled")}
                      </Badge>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground break-all">
                    {member.email}
                  </span>
                </div>
                {editable && !me && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === member.id || !!member.disabled}
                      onClick={() =>
                        patch(member, { org_admin: !member.org_admin })
                      }
                    >
                      {translate(
                        member.org_admin
                          ? "resources.deals.org.remove_admin"
                          : "resources.deals.org.make_admin",
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        busy === member.id || (!!member.disabled && seats.full)
                      }
                      title={
                        member.disabled && seats.full
                          ? translate("resources.deals.org.full_hint", {
                              limit: seats.limit,
                            })
                          : undefined
                      }
                      onClick={() =>
                        patch(member, {
                          disabled: !member.disabled,
                          org_admin: member.disabled ? member.org_admin : false,
                        })
                      }
                    >
                      {translate(
                        member.disabled
                          ? "resources.deals.org.enable"
                          : "resources.deals.org.disable",
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
      {editable &&
        (seats.full ? (
          <p className="text-muted-foreground rounded-lg border p-4 text-sm">
            {translate("resources.deals.org.full_hint", {
              limit: seats.limit,
            })}
          </p>
        ) : (
          <InviteMember />
        ))}
    </section>
  );
};

const InviteMember = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [sending, setSending] = useState(false);
  const complete =
    form.first_name.trim() && form.last_name.trim() && form.email.includes("@");
  const invite = async () => {
    setSending(true);
    const { error } = await getSupabaseClient().functions.invoke("users", {
      method: "POST",
      body: { org_member: true, ...form },
    });
    setSending(false);
    if (error) return notify(await errorMessage(error), { type: "error" });
    notify("resources.deals.org.invited", {
      type: "success",
      messageArgs: { email: form.email },
    });
    setForm({ first_name: "", last_name: "", email: "" });
    refresh();
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-4">
      <h4 className="text-sm font-semibold">
        {translate("resources.deals.org.invite")}
      </h4>
      <p className="text-sm text-muted-foreground">
        {translate("resources.deals.org.invite_hint")}
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        {(["first_name", "last_name", "email"] as const).map((field) => (
          <div key={field} className="flex flex-col gap-1">
            <Label htmlFor={`invite-${field}`}>
              {translate(`resources.sales.fields.${field}`)}
            </Label>
            <Input
              id={`invite-${field}`}
              type={field === "email" ? "email" : "text"}
              value={form[field]}
              onChange={(e) => setForm({ ...form, [field]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <Button
        className="self-start"
        disabled={!complete || sending}
        onClick={invite}
      >
        {translate("resources.deals.org.send_invite")}
      </Button>
    </div>
  );
};

type StageRow = { value?: string; label: string; closed: boolean };

const OrgPipelineEditor = ({
  org,
  editable,
}: {
  org: PartnerOrg;
  editable: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [rows, setRows] = useState<StageRow[]>(() =>
    org.deal_stages.map((stage) => ({
      ...stage,
      closed: org.deal_closed_stages.includes(stage.value),
    })),
  );
  const [saving, setSaving] = useState(false);
  // Affaires propres de l'organisation, archivées comprises, pour signaler les étapes occupées avant d'enregistrer.
  const { data: deals } = useGetList<Deal>("deals", {
    filter: { org_id: org.id },
    pagination: { page: 1, perPage: 1000 },
  });
  const counts = stageCounts(deals ?? []);
  const update = (index: number, changes: Partial<StageRow>) =>
    setRows(rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  const move = (index: number, by: number) => {
    const next = [...rows];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    setRows(next);
  };
  const save = async () => {
    // Lignes sans nom ignorées ; les cases « fin » suivent leur ligne.
    const kept = rows.filter((row) => row.label.trim());
    const stages = normalizeStages(kept);
    const closed = stages
      .filter((_, i) => kept[i].closed)
      .map((stage) => stage.value);
    setSaving(true);
    const { error } = await getSupabaseClient().rpc("set_org_pipeline", {
      stages,
      closed,
    });
    setSaving(false);
    if (error) return notify(await errorMessage(error), { type: "error" });
    notify("resources.deals.org.saved", { type: "success" });
    refresh();
  };
  return (
    <section id="pipeline" className="flex flex-col gap-3 scroll-mt-4">
      <h3 className="text-base font-semibold">
        {translate("resources.deals.org.pipeline")}
      </h3>
      <p className="text-sm text-muted-foreground">
        {translate("resources.deals.org.pipeline_hint")}
      </p>
      <Card className="py-0">
        <CardContent className="flex flex-col divide-y px-0">
          {rows.map((row, index) => (
            <div
              key={row.value ?? `new-${index}`}
              className="flex flex-wrap items-center gap-2 px-4 py-2"
            >
              {editable ? (
                <Input
                  aria-label={translate("resources.deals.org.stage_name")}
                  className="flex-1 min-w-40"
                  value={row.label}
                  onChange={(e) => update(index, { label: e.target.value })}
                />
              ) : (
                <span className="flex-1">{row.label}</span>
              )}
              {!!row.value && !!counts[row.value] && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {translate("resources.deals.org.stage_count", {
                    smart_count: counts[row.value],
                  })}
                </span>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={row.closed}
                  disabled={!editable}
                  onCheckedChange={(v) => update(index, { closed: v === true })}
                />
                {translate("resources.deals.org.closed")}
              </label>
              {editable && (
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === 0}
                    aria-label={translate("resources.deals.org.up")}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === rows.length - 1}
                    aria-label={translate("resources.deals.org.down")}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown />
                  </Button>
                  {/* Étape occupée : corbeille bloquée, la raison au survol (un bouton désactivé n'en affiche pas). */}
                  <span
                    title={
                      row.value && counts[row.value]
                        ? translate("resources.deals.org.stage_used", {
                            smart_count: counts[row.value],
                          })
                        : undefined
                    }
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={
                        rows.length === 1 || !!(row.value && counts[row.value])
                      }
                      aria-label={translate("resources.deals.org.remove_stage")}
                      onClick={() =>
                        setRows(rows.filter((_, i) => i !== index))
                      }
                    >
                      <Trash2 />
                    </Button>
                  </span>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setRows([...rows, { label: "", closed: false }])}
          >
            <Plus />
            {translate("resources.deals.org.add_stage")}
          </Button>
          <Button disabled={saving} onClick={save}>
            {translate("ra.action.save")}
          </Button>
        </div>
      )}
    </section>
  );
};
